import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { LoyaltyRewardsRail, theme } from '@sg/garnish';

import type { ChallengeRewardsQuery } from '@order/graphql';
import { useRewardsWithDerivedData } from '@order/hooks';

import { RewardDetailsModal } from '../../../../components/RewardDetailsModal';

export const SweetpassHomeRewardsRail = ({
  content,
  rewards,
  isFetching,
}: SweetpassHomeRewardsRailProps) => {
  const rewardsWithExtraDetails = useRewardsWithDerivedData(rewards);

  const [selected, setSelected] = useState<string | undefined>();

  const handleRewardPress = useCallback(
    (id: string) => {
      if (selected === id) {
        setSelected(undefined);

        return;
      }

      setSelected(id);
    },
    [selected],
  );

  const resetActiveRewardId = useCallback(() => {
    setSelected(undefined);
  }, []);

  const activeReward = useMemo(
    () => rewards?.find((reward) => reward.id === selected),
    [rewards, selected],
  );

  return (
    <View style={styles.container}>
      <LoyaltyRewardsRail
        isLoading={isFetching}
        rewards={rewardsWithExtraDetails}
        headerVariation="display"
        availableCount={rewardsWithExtraDetails?.length}
        headerText={content.titleText}
        subtitle={content.bodyText}
        emptyStateImage={{ uri: content.emptyContentImage.url }}
        emptyStateText={content.emptyContentText}
        selectedRewardId={selected}
        onPress={handleRewardPress}
      />

      <RewardDetailsModal
        reward={activeReward}
        onDismiss={resetActiveRewardId}
      />
    </View>
  );
};

// ––– STYLES –––––––––––––––––––––––––––––

const styles = StyleSheet.create({
  container: {
    marginBottom: theme.spacing['6'],
  },
});

// ––– TYPES –––––––––––––––––––––––––––––

type SweetpassHomeRewardsRailProps = {
  readonly content: SweetpassHomeRewardsRailContent;
  readonly isFetching: boolean;
  readonly rewards: ChallengeRewardsQuery['challengeRewards'];
};

type SweetpassHomeRewardsRailContent = {
  readonly type: 'sweetpassHomeRewardsRail';
  readonly id: string;
  readonly titleText: string;
  readonly bodyText?: string;
  readonly emptyContentText: string;
  readonly emptyContentImage: {
    readonly url: string;
    readonly description: string;
  };
};
